import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingBagIcon, HeartIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import { productsAPI, wishlistAPI } from "../services/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { resolveImageUrl } from "../utils/image";
import toast from "react-hot-toast";

interface Variant {
  id: string;
  color_name: string;
  hex_code: string | null;
  size: string;
  stock: number;
}

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart, isLoading: cartLoading } = useCart();
  const { isAuthenticated } = useAuth();

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [wishlisted, setWishlisted] = useState(false);

  useEffect(() => {
    if (!slug) return;
    productsAPI.getBySlug(slug)
      .then(res => { setProduct(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  // ── Derived variant data ────────────────────────────────────────────────
  const variants: Variant[] = product?.variants ?? [];

  /** Unique colors extracted from variants (preserves first-seen hex). */
  const uniqueColors = useMemo(() => {
    const seen = new Map<string, { name: string; hex_code: string | null; totalStock: number }>();
    for (const v of variants) {
      if (!seen.has(v.color_name)) {
        seen.set(v.color_name, { name: v.color_name, hex_code: v.hex_code, totalStock: 0 });
      }
      seen.get(v.color_name)!.totalStock += v.stock;
    }
    return Array.from(seen.values());
  }, [variants]);

  const hasColors = uniqueColors.length > 0 && uniqueColors.some(c => c.name !== "");

  /**
   * Sizes available for the currently selected color.
   * If no color is selected (or product has no colors), show all sizes from variants.
   */
  const sizesForColor = useMemo(() => {
    const colorFilter = hasColors ? selectedColor : "";
    return variants.filter(v => v.color_name === colorFilter);
  }, [variants, selectedColor, hasColors]);

  /** Stock for the exact (selectedColor, selectedSize) combination. */
  const selectedVariant = useMemo(
    () => sizesForColor.find(v => v.size === selectedSize) ?? null,
    [sizesForColor, selectedSize]
  );

  const isOutOfStock = product?.total_stock === 0;

  /** Images filtered by selected color — must be before any early return. */
  const allImages: any[] = product?.images ?? [];
  const images = useMemo(() => {
    if (!selectedColor) return allImages;
    const forColor = allImages.filter(img => img.color_name === selectedColor);
    const untagged  = allImages.filter(img => !img.color_name);
    const filtered  = [...forColor, ...untagged];
    return filtered.length > 0 ? filtered : allImages;
  }, [allImages, selectedColor]);

  // Reset size when color changes
  const handleSelectColor = (colorName: string) => {
    setSelectedColor(colorName);
    setSelectedSize("");      // size must be re-chosen per color
    setSelectedImage(0);      // jump to first image for this color
    setQuantity(1);
  };

  // ── Cart handlers ──────────────────────────────────────────────────────
  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to add to cart");
      navigate("/login");
      return;
    }
    if (hasColors && !selectedColor) {
      toast.error("Please select a color");
      return;
    }
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }
    await addToCart(product.id, selectedSize, quantity, selectedColor || undefined);
  };

  const handleBuyNow = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to continue");
      navigate("/login");
      return;
    }
    if (hasColors && !selectedColor) {
      toast.error("Please select a color");
      return;
    }
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }
    await addToCart(product.id, selectedSize, quantity, selectedColor || undefined);
    navigate("/checkout");
  };

  const toggleWishlist = async () => {
    if (!isAuthenticated) { toast.error("Please login to wishlist"); return; }
    if (wishlisted) {
      await wishlistAPI.remove(product.id);
      setWishlisted(false);
    } else {
      await wishlistAPI.add(product.id);
      setWishlisted(true);
      toast.success("Added to wishlist!");
    }
  };

  // ── Loading / not found ────────────────────────────────────────────────
  if (loading) return <LoadingSpinner className="py-40" size="lg" />;
  if (!product) return (
    <div className="text-center py-40">
      <p className="text-2xl mb-4">🔍</p>
      <p className="text-gray-500">Product not found</p>
    </div>
  );

  const currentImageRaw = images[selectedImage] || { url: "https://via.placeholder.com/600x700/f9a8d4/ec4899?text=Dress" };
  const currentImage = { ...currentImageRaw, url: resolveImageUrl(currentImageRaw.url) };
  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <>
      <Helmet>
        <title>{product.name} – Kushi Fashions</title>
        <meta name="description" content={product.description} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 text-sm">
          <ChevronLeftIcon className="h-4 w-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* ── Images ── */}
          <div>
            <div className="relative rounded-2xl overflow-hidden bg-gray-50 aspect-[3/4]">
              <img src={currentImage.url} alt={product.name} className="w-full h-full object-cover" />
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage(i => Math.max(0, i - 1))}
                    disabled={selectedImage === 0}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow hover:bg-white transition-colors"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setSelectedImage(i => Math.min(images.length - 1, i + 1))}
                    disabled={selectedImage === images.length - 1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow hover:bg-white transition-colors"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
                {images.map((img: any, idx: number) => (
                  <button
                    key={img.id || idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-12 h-14 rounded-md overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? "border-primary-500" : "border-gray-200"
                    }`}
                  >
                    <img src={resolveImageUrl(img.url)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product Info ── */}
          <div className="space-y-5">
            {product.category && (
              <span className="badge bg-primary-50 text-primary-700">{product.category}</span>
            )}
            <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>

            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-primary-600">
                ₹{Number(product.price).toLocaleString("en-IN")}
              </span>
              {product.original_price && (
                <>
                  <span className="text-xl text-gray-400 line-through">
                    ₹{Number(product.original_price).toLocaleString("en-IN")}
                  </span>
                  <span className="badge bg-green-100 text-green-700">{discount}% off</span>
                </>
              )}
            </div>

            {product.description && (
              <p className="text-gray-600 leading-relaxed">{product.description}</p>
            )}

            {product.material && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-gray-400">Material</p>
                <p className="font-medium text-gray-700">{product.material}</p>
              </div>
            )}

            {/* ── Color selector ── */}
            {hasColors && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <p className="font-semibold text-gray-700">Color</p>
                  {selectedColor
                    ? <span className="text-sm text-gray-600 font-medium">— {selectedColor}</span>
                    : <span className="text-sm text-gray-400">— pick a color</span>
                  }
                </div>
                <div className="flex gap-3 flex-wrap">
                  {uniqueColors.map(color => {
                    const outOfStock = color.totalStock === 0;
                    const isSelected = selectedColor === color.name;
                    return (
                      <button
                        key={color.name}
                        onClick={() => !outOfStock && handleSelectColor(color.name)}
                        title={outOfStock ? `${color.name} — Out of stock` : color.name}
                        disabled={outOfStock}
                        className={`relative w-10 h-10 rounded-full border-2 transition-all ${
                          isSelected
                            ? "border-primary-600 scale-110 shadow-lg"
                            : outOfStock
                            ? "border-gray-200 opacity-40 cursor-not-allowed"
                            : "border-gray-300 hover:border-gray-500 hover:scale-105"
                        }`}
                        style={{ backgroundColor: color.hex_code || "#ccc" }}
                      >
                        {outOfStock && (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="block w-full h-0.5 bg-gray-400 rotate-45 rounded" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedColor && (
                  <p className="text-xs text-gray-400 mt-2">
                    {uniqueColors.find(c => c.name === selectedColor)?.totalStock ?? 0} pieces available in {selectedColor}
                  </p>
                )}
              </div>
            )}

            {/* ── Size selector ── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-gray-700">
                  Size
                  {hasColors && !selectedColor && (
                    <span className="text-xs text-gray-400 font-normal ml-1">(select a color first)</span>
                  )}
                </p>
                <button className="text-sm text-primary-600 hover:underline">Size Guide</button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {sizesForColor.length === 0 && hasColors && !selectedColor ? (
                  /* No color chosen yet — show placeholder pills */
                  ["XS","S","M","L","XL","XXL"].map(s => (
                    <button key={s} disabled
                      className="px-4 py-2 rounded-lg border text-sm font-medium border-gray-200 text-gray-300 cursor-not-allowed"
                    >{s}</button>
                  ))
                ) : (
                  sizesForColor.map(v => {
                    const outOfStock = v.stock === 0;
                    const isSelected = selectedSize === v.size;
                    return (
                      <button
                        key={v.size}
                        onClick={() => !outOfStock && setSelectedSize(v.size)}
                        disabled={outOfStock}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                          isSelected
                            ? "border-primary-600 bg-primary-600 text-white"
                            : outOfStock
                            ? "border-gray-200 text-gray-300 cursor-not-allowed line-through"
                            : "border-gray-300 text-gray-700 hover:border-primary-300"
                        }`}
                      >
                        {v.size}
                        {outOfStock && <span className="text-xs ml-1">(Out)</span>}
                      </button>
                    );
                  })
                )}
              </div>
              {selectedVariant && selectedVariant.stock > 0 && selectedVariant.stock < 10 && (
                <p className="text-orange-500 text-xs mt-2">Only {selectedVariant.stock} left in this size!</p>
              )}
            </div>

            {/* ── Quantity ── */}
            <div className="flex items-center gap-3">
              <p className="font-semibold text-gray-700">Quantity</p>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-2 hover:bg-gray-50 text-lg font-medium">−</button>
                <span className="px-4 py-2 font-medium text-gray-700">{quantity}</span>
                <button
                  onClick={() => setQuantity(q => selectedVariant ? Math.min(selectedVariant.stock, q + 1) : q + 1)}
                  className="px-3 py-2 hover:bg-gray-50 text-lg font-medium"
                >+</button>
              </div>
              {selectedVariant && (
                <span className="text-xs text-gray-400">{selectedVariant.stock} in stock</span>
              )}
            </div>

            {/* ── CTA ── */}
            <div className="flex gap-3">
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock || cartLoading}
                className="flex-1 btn-secondary py-3 flex items-center justify-center gap-2"
              >
                <ShoppingBagIcon className="h-5 w-5" />
                {isOutOfStock ? "Out of Stock" : "Add to Cart"}
              </button>
              <button
                onClick={handleBuyNow}
                disabled={isOutOfStock || cartLoading}
                className="flex-1 btn-primary py-3"
              >
                Buy Now
              </button>
              <button
                onClick={toggleWishlist}
                className="p-3 border border-gray-300 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                {wishlisted
                  ? <HeartSolidIcon className="h-5 w-5 text-primary-600" />
                  : <HeartIcon className="h-5 w-5 text-gray-600" />
                }
              </button>
            </div>

            {/* ── Policies ── */}
            <div className="border-t border-gray-100 pt-4 space-y-2 text-sm text-gray-500">
              <p>🚚 Free delivery on orders above ₹999</p>
              <p>↩️ 7-day easy return policy</p>
              <p>🔒 Secure payment via Razorpay</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
